import { TableStatuses, TableStore } from 'components/NewTable/store/TableStore'
import { ICardDetail } from 'components/DetailCard/DetailCard/DetailCard'
import { ContentType, MessageStatisticsResponse } from 'src/generated/graphql'
import { DetailCardsWrapper } from 'components/DetailCard/DetailCardsWrapper'
import { IStatisticMessage } from 'src/types/IDetailMessage'
import { detailsCards } from 'components/DetailCard/variants'
import { Currencies, numberString } from 'src/util/functions'

type MessageStatisticCardsProps = {
  messageStatistic: MessageStatisticsResponse | undefined | null
  type: ContentType
  tableStore: TableStore<IStatisticMessage>
  withToday?: boolean
  cardFilter?: (card: ICardDetail) => boolean
}

export const MessageStatisticCards = ({
  messageStatistic,
  type,
  tableStore,
  withToday,
  cardFilter,
}: MessageStatisticCardsProps) => {
  const totalStatistics = messageStatistic?.totalStatistics
  const percentageStatistics = messageStatistic?.percentageStatistics
  const voiceTransferMessageStatistics =
    messageStatistic?.voiceTransferMessageStatistics
  const shortUrlClickStatistics = messageStatistic?.shortUrlClickStatistics
  const conversionStatistics = messageStatistic?.conversionStatistics
  const todayStatistics = messageStatistic?.todayStatistics

  const totalCount = totalStatistics?.totalCount || 0
  const successCount = totalStatistics?.successCount || 0
  const unsubscribedCount = totalStatistics?.unsubscribedCount || 0
  const pendingCount = totalStatistics?.pendingCount || 0
  const failedCount = totalStatistics?.failedCount || 0
  const callbackCount = totalStatistics?.callbackCount || 0
  const voiceTransferCount = voiceTransferMessageStatistics?.total || 0

  const successPercentage = percentageStatistics?.successPercentage || 0
  const unsubscribedPercentage =
    percentageStatistics?.unsubscribedPercentage || 0
  const failedPercentage = percentageStatistics?.failedPercentage || 0
  const pendingPercentage = percentageStatistics?.pendingPercentage || 0
  const callbackPercentage = percentageStatistics?.callbackPercentage || 0
  const voiceTransferPercentage =
    voiceTransferMessageStatistics?.percentage || 0

  const totalCountToday = todayStatistics?.totalCount || 0
  const successCountToday = todayStatistics?.successCount || 0
  const unsubscribedCountToday = todayStatistics?.unsubscribedCount || 0
  const pendingCountToday = todayStatistics?.pendingCount || 0
  const failedCountToday = todayStatistics?.failedCount || 0
  const callbackCountToday = todayStatistics?.callbackCount || 0
  const voiceTransferCountToday = voiceTransferMessageStatistics?.today || 0

  const cardObj = {
    [TableStatuses.totalCount]: detailsCards.total({
      count: totalCount,
      today: withToday ? totalCountToday : undefined,
    }),

    [TableStatuses.successCount]: detailsCards.success({
      count: successCount,
      percent: successPercentage,
      today: withToday ? successCountToday : undefined,
    }),
    [TableStatuses.clicksCount]:
      shortUrlClickStatistics &&
      detailsCards.shortUrlClickStatistics({
        shortUrlClickStatistics,
        card: {
          today: withToday
            ? shortUrlClickStatistics?.totalVisitsToday
            : undefined,
        },
      }),
    [TableStatuses.converted]: detailsCards.custom({
      title: 'Converted',
      status: TableStatuses.converted,
      percent: conversionStatistics?.percentage || 0,
      count: numberString({
        val: conversionStatistics?.total || 0,
      }),
      today: withToday ? 0 : undefined,
    }),
    [TableStatuses.revenue]: detailsCards.custom({
      noSelectCard: true,
      title: 'Revenue',
      status: TableStatuses.revenue,
      hidePercent: true,
      count: numberString({
        val: conversionStatistics?.revenue || 0,
        currency: Currencies.usd,
      }),
      today: withToday
        ? numberString({
            val: conversionStatistics?.todayConversionStatistics?.revenue || 0,
            currency: Currencies.usd,
          })
        : undefined,
    }),
    [TableStatuses.totalCosts]: detailsCards.custom({
      noSelectCard: true,
      title: 'Cost',
      status: TableStatuses.totalCosts,
      hidePercent: true,
      count: numberString({
        val: conversionStatistics?.totalCosts || 0,
        currency: Currencies.usd,
      }),
      today: withToday
        ? numberString({
            val:
              conversionStatistics?.todayConversionStatistics?.totalCosts || 0,
            currency: Currencies.usd,
          })
        : undefined,
    }),
    [TableStatuses.roi]: detailsCards.custom({
      noSelectCard: true,
      title: 'ROI',
      status: TableStatuses.roi,
      hidePercent: true,
      count: `${numberString({
        val: conversionStatistics?.roi || 0,
        maximumFractionDigits: 1,
      })}%`,
      today: withToday
        ? `${numberString({
            val: conversionStatistics?.todayConversionStatistics?.roi || 0,
            maximumFractionDigits: 1,
          })}%`
        : undefined,
    }),
    [TableStatuses.unsubscribedCount]: detailsCards.unsubscribed({
      count: unsubscribedCount,
      percent: unsubscribedPercentage,
      today: withToday ? unsubscribedCountToday : undefined,
    }),
    [TableStatuses.pendingCount]: detailsCards.pending({
      count: pendingCount,
      percent: pendingPercentage,
      today: withToday ? pendingCountToday : undefined,
    }),
    [TableStatuses.failedCount]: detailsCards.failed({
      count: failedCount,
      percent: failedPercentage,
      today: withToday ? failedCountToday : undefined,
    }),
    [TableStatuses.callBacks]: detailsCards.callBacks({
      count: callbackCount,
      percent: callbackPercentage,
      today: withToday ? callbackCountToday : undefined,
    }),
    [TableStatuses.voiceTransfers]: detailsCards.voiceTransfers({
      count: voiceTransferCount,
      percent: voiceTransferPercentage,
      today: withToday ? voiceTransferCountToday : undefined,
    }),
  }

  const getCards = (): ICardDetail[] => {
    if (type === ContentType.Ringless) {
      return [
        cardObj[TableStatuses.totalCount],
        cardObj[TableStatuses.pendingCount],
        cardObj[TableStatuses.successCount],
        cardObj[TableStatuses.callBacks],
        cardObj[TableStatuses.failedCount],
      ]
    }
    if (type === ContentType.Voice) {
      return [
        cardObj[TableStatuses.totalCount],
        cardObj[TableStatuses.pendingCount],
        cardObj[TableStatuses.successCount],
        cardObj[TableStatuses.voiceTransfers],
        cardObj[TableStatuses.failedCount],
        cardObj[TableStatuses.unsubscribedCount],
      ].filter((card) => Boolean(card)) as ICardDetail[]
    }
    return [
      cardObj[TableStatuses.totalCount],
      cardObj[TableStatuses.pendingCount],
      cardObj[TableStatuses.successCount],
      shortUrlClickStatistics?.shortUrlVisits?.length &&
        cardObj[TableStatuses.clicksCount],
      shortUrlClickStatistics?.shortUrlVisits?.length &&
        conversionStatistics &&
        cardObj[TableStatuses.converted],
      shortUrlClickStatistics?.shortUrlVisits?.length &&
        conversionStatistics &&
        cardObj[TableStatuses.revenue],
      shortUrlClickStatistics?.shortUrlVisits?.length &&
        conversionStatistics &&
        cardObj[TableStatuses.totalCosts],
      shortUrlClickStatistics?.shortUrlVisits?.length &&
        conversionStatistics &&
        cardObj[TableStatuses.roi],
      cardObj[TableStatuses.failedCount],
      cardObj[TableStatuses.unsubscribedCount],
    ].filter((card) => Boolean(card)) as ICardDetail[]
  }

  return (
    <DetailCardsWrapper
      cards={getCards().filter((card) =>
        cardFilter ? cardFilter(card) : true
      )}
      tableStore={tableStore}
    />
  )
}
